export interface CountItem {
  value: string;
  count: number;
}

export interface Breakdown {
  items: CountItem[];
  total: number;
}

export interface EquitySummary {
  answeredBy: number;
  booleans: { key: string; label: string; yes: number; answered: number }[];
  genderIdentity: { items: CountItem[]; total: number };
}

export interface Who {
  attendees: number;
  byRole: CountItem[];
  byAudience: CountItem[];
  byRegion: Breakdown;
  byCareerStage: Breakdown;
  byDiscipline: Breakdown;
  equity: EquitySummary;
}

export interface Figures {
  countsOnly?: number;
  onList: number;
  registered: number;
  confirmed: number;
  declined: number;
  waitlisted: number;
  attended: number;
  inPerson: number;
  virtual: number;
  personDays: number;
  byDay: { date: string; count: number }[];
  dayUnknown: number;
  peakDay: number;
  noShows: number;
  noShowRate: number | null;
  attendanceRate: number | null;
  attendedOnList: number;
  walkIns: number;
  attendedWithoutListEntry: number;
  capacity: number | null;
  capacityUse: number | null;
}

export interface Curve {
  eventId: string;
  title: string;
  year: number;
  dated: number;
  undated: number;
  medianDaysBefore: number | null;
  earliestDaysBefore: number | null;
  points: { daysBefore: number; count: number; share: number | null }[];
}

export interface Logistics {
  expectedInPerson: number;
  withNeedsRecorded: number;
  hotel: {
    travellers: number;
    roomNights: number;
    nightsPerTraveller: number | null;
    nightsDistribution: CountItem[];
    nights: { date: string; rooms: number; block: number | null; overBlock: number | null }[];
    peakRooms: number;
    block: number | null;
    blockRoomNights: number | null;
    peakUse: number | null;
  };
  travelModes: CountItem[];
  attendanceModes: CountItem[];
  parking: number;
  dietary: number;
  accessibility: number;
  dietaryGroups?: CountItem[];
}

export interface KindMoney {
  count: number;
  withAmount: number;
  totalCents: number;
  paid: number;
  open: number;
  medianDaysToPaid: number | null;
}

export interface Money {
  entries: number;
  cancelled: number;
  reimbursement: KindMoney;
  honorarium: KindMoney;
  gift_card: KindMoney;
  registration: KindMoney;
  prize?: KindMoney;
  totalCents: number;
  missingAmounts: number;
  vendors: number;
  otherCostActualCents: number;
  otherCostCommittedCents: number;
  revenueActualCents: number;
  allCostCents: number;
  costPerInPersonAttendeeCents: number | null;
}

export interface InsightEvent {
  id: string;
  title: string;
  kind: string;
  mode: string;
  city: string | null;
  venue: string | null;
  year: number;
  startsAt: string;
  endsAt: string;
  days: string[];
  ended: boolean;
  closedAt: string | null;
  clonedFrom: { eventId: string; title: string } | null;
  figures: Figures;
  firstTime: number;
  returning: number;
  who: Who;
  curve: Curve;
  logistics: Logistics;
  program: {
    sessions: number;
    speakers: number;
    speakersByAffiliation: CountItem[] | null;
    sessionRating: number | null;
    ratedSessions: number;
  } | null;
  evaluation: { responses: number; numeric: { key: string; label: string; answered: number; mean: number | null }[] };
  eventbrite: { grossCents?: number | null; netCents?: number | null; tickets: number | null } | null;
  money?: Money;
}

export interface InsightYear {
  year: number;
  events: number;
  countsOnly?: number;
  registered: number;
  confirmed: number;
  declined: number;
  attended: number;
  inPerson: number;
  virtual: number;
  personDays: number;
  noShows: number;
  noShowRate: number | null;
  capacityUse: number | null;
  uniqueAttendees: number;
  firstTime: number;
  returning: number;
  who: Who;
  logistics: {
    expectedInPerson: number;
    hotelTravellers: number;
    roomNights: number;
    peakRooms: number;
    block: number | null;
    parking: number;
    dietary: number;
  };
  money?: {
    totalCents: number;
    reimbursements: number;
    reimbursementCents: number;
    honorariaCents: number;
    giftCardCents: number;
    missingAmounts: number;
    vendorCostCents: number;
    allCostCents: number;
    costPerInPersonAttendeeCents: number | null;
  };
}

export interface Insights {
  filters: { from: string | null; to: string | null; kind: string[] | null };
  moneyVisible: boolean;
  events: InsightEvent[];
  years: InsightYear[];
  retention: {
    people: number;
    twoPlus: number;
    members: number;
    membersTwoPlus: number;
    distribution: CountItem[];
    memberDistribution: CountItem[];
  };
  cohorts: {
    cohort: number;
    size: number;
    attendedAny: number;
    attendedAnyRate: number | null;
    years: { year: number; tenureYear: number; attended: number; rate: number | null }[];
  }[];
  notes: Record<string, string>;
  unavailable: { key: string; reason: string }[];
  kinds: string[];
  allYears: number[];
}

export const eventLabel = (e: { year: number; title: string }) =>
  `${e.year} · ${e.title.length > 28 ? `${e.title.slice(0, 27)}…` : e.title}`;

export const SERIES = ['#231F20', '#3EC2FF', '#FF792E', '#2BE081', '#FF96C2', '#6E7A00', '#0B5A80', '#1A6B42'];

export const rate = (r: number | null | undefined, digits = 0) =>
  r === null || r === undefined ? '—' : `${(r * 100).toFixed(digits)}%`;

export const dollars = (cents: number | null | undefined) =>
  cents === null || cents === undefined
    ? '—'
    : (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

const CSV_FORMULA = /^[=+\-@\t\r]/;

export function downloadCsv(name: string, rows: (string | number | null | undefined)[][]) {
  const cell = (v: string | number | null | undefined) => {
    let s = v === null || v === undefined ? '' : String(v);
    if (typeof v === 'string' && CSV_FORMULA.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csv = rows.map((r) => r.map(cell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `divert-events-${name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** The date nearest `requested` on the weekday of `sourceFirst` (as the server does). */
export function alignedStart(sourceFirst: string, requested: string) {
  const wd = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();
  let diff = wd(sourceFirst) - wd(requested);
  if (diff > 3) diff -= 7;
  if (diff < -3) diff += 7;
  const d = new Date(`${requested}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}
